import { inject } from "vue";

export function useAssets() {
  const { modelConfigs } = inject("constants");

  /**
   * Find the icon based on the prescription rows.
   * @param {Object} prescriptionRows - The prescription rows object.
   * @returns {Array} - An array of icons.
   */
  function findIconByPrescriptionRows(prescriptionRows) {
    const application = (prescriptionRows.application || "").toLowerCase();
    const zoneNum = normalizeZoneNumber(application, prescriptionRows.zone_number);

    const icons = [];

    const normalizedApp = normalizeApplication(application, zoneNum)

    const match = Object.values(modelConfigs).find(config =>
      config.application.toLowerCase().normalize("NFD").replace(/\s+/g, '') === normalizedApp &&
      (! config.zone_number || config.zone_number.toLowerCase() === zoneNum)
    );

    if (match) {
      icons.push(match.icon);
      return icons;
    }

    if (application === "emiarcata" && !isNaN(zoneNum)) {
      icons.push(`emiarch/emiarch-${zoneNum}`);
      return icons;
    }

    if (application === "sestante" && !isNaN(zoneNum)) {
      icons.push(`sest/sest-${zoneNum}`);
      return icons;
    }

    if ((application === "dente" || application === "") && !isNaN(zoneNum)) {
      icons.push(`teeth/teeth-${zoneNum}`);
      return icons;
    }

    console.log("findIconByPrescriptionRows: application not found");
    icons.push("mouth/mouth");

    return icons;
  }

  /**
   * Get the label name by asset slug.
   * @param {string} slug - The asset slug.
   * @returns {string} - The label name.
   */
  function getLabelsNameByAsset(slug) {
    const found = Object.values(modelConfigs).find(
      config => config.slug === slug || config.assetType === slug
    );

    return found ? found.name : slug;
  }

  /**
 * Normalizes the zoneNumber based on the application type.
 * If application is "arcata", converts "up" to "sup" and "down" to "inf".
 * Otherwise, returns the zoneNumber normalized to lowercase.
 *
 * @param {string} application - The type of application (e.g., "arcata", "emiarcata")
 * @param {string} zoneNumber - The original zone number value
 * @returns {string} Normalized zone number
 */
  function normalizeZoneNumber(application, zoneNumber) {
    const normalized = (zoneNumber || "").toLowerCase();

    if (application === "arcata") {
      if (normalized === "up") return "sup";
      if (normalized === "down") return "inf";
    }

    return normalized;
  }

  /**
   * Normalizes the application string by removing uppercase letters, spaces, and accents.
   * If application is "emiarcata" and zoneNumber is "sup" or "inf", treats it as "arcata".
   *
   * @param {string} application - The application name
   * @param {string} zoneNumber - The normalized zone number used for conditional logic
   * @returns {string} Normalized application string
   */
  function normalizeApplication(application, zoneNumber) {
    let normalizeApp =  (application || "").toLowerCase().normalize("NFD").replace(/\s+/g, '');

    if (normalizeApp === "emiarcata" && (zoneNumber === "sup" || zoneNumber === 'inf')) {
      normalizeApp = "arcata";
    }

    return normalizeApp;
  }


  return { findIconByPrescriptionRows, getLabelsNameByAsset };
}
